import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginAdmin, verifyAdminLoginCode } from '@/lib/api';

const ADMIN_TOKEN_KEY = 'medicaclic_admin_token';
const ADMIN_USER_KEY = 'medicaclic_admin_user';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!challengeId) {
        const response = await loginAdmin({ email, password });
        setChallengeId(response.challenge_id);
        setError('');
      } else {
        const response = await verifyAdminLoginCode(challengeId, verificationCode);
        localStorage.setItem(ADMIN_TOKEN_KEY, response.token);
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(response.user));
        navigate('/admin');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de connexion';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg ring-1 ring-slate-200">
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Admin</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">{challengeId ? 'Vérification' : 'Connexion'}</h1>
          {challengeId && <p className="mt-2 text-sm text-slate-600">Saisissez le code envoyé à {email}.</p>}
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {!challengeId ? (
            <>
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">Email</label>
                <input id="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200" placeholder="admin@medicaclic.com" required />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">Mot de passe</label>
                <input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200" placeholder="••••••••" required />
              </div>
            </>
          ) : (
            <div>
              <label htmlFor="verification-code" className="mb-2 block text-sm font-medium text-slate-700">Code de vérification</label>
              <input
                id="verification-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={verificationCode}
                onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-3 text-center font-mono text-2xl tracking-[0.35em] text-slate-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                placeholder="000000"
                required
              />
              <p className="mt-2 text-xs text-slate-500">Le code expire après 5 minutes.</p>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? 'Vérification...' : challengeId ? 'Confirmer et se connecter' : 'Continuer'}
          </button>
        </form>

        {challengeId && (
          <button type="button" onClick={() => { setChallengeId(''); setVerificationCode(''); setError(''); }} className="mt-4 w-full text-sm font-medium text-slate-600 transition hover:text-slate-900">
            Recommencer la connexion
          </button>
        )}
      </div>
    </div>
  );
}
